import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { InactivityLogoutWatcher } from "@/components/auth/inactivity-logout-watcher";
import { ProtectedSidebarLayout } from "@/components/layouts/protected-sidebar-layout";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useComplianceSyncRefresh } from "@/hooks/use-compliance-sync-refresh";
import { useFollowUpActionSyncRefresh } from "@/hooks/use-follow-up-action-sync-refresh";
import { useMonitoringSyncRefresh } from "@/hooks/use-monitoring-sync-refresh";

// Routes that nursery staff (non-managers) can access.
// `/compliance/instance` is included so non-managers (LEADER, DEPUTY_MANAGER,
// PRACTITIONER) can open and complete compliance tasks assigned to them or
// their role — the API's ZenStack policy gates the actual data access. The
// other `/compliance/*` routes (definition CRUD) remain manager-only by
// omission.
const NURSERY_STAFF_ALLOWED_ROUTES = [
  "/dashboard",
  "/checklists",
  "/logs",
  "/follow-up-actions",
  // Task Manager: open to all nursery staff. The API's ZenStack policy narrows
  // the rows (and the tab counts) to what each user may see, so a practitioner
  // gets their own work rather than the whole nursery's. The "+ Add Task"
  // action is gated separately, in the page.
  "/task-manager",
  "/compliance/instance",
  // Event records: leaders+ report events and view records. As with
  // `/compliance/instance`, the route is open to all nursery staff and the
  // API's ZenStack policy / role checks gate the actual create + read.
  "/event-records",
  // Fire extinguisher checks (NAU-242): any nursery member may submit a check
  // from the Logs tab. As above, the route is open to all nursery staff and
  // the API's ZenStack policy gates the data — history shows all checks to
  // Manager+ and only the submitter's own to other staff.
  "/fire-extinguisher-checks",
  // Acknowledgements (NAU-410): this route MUST be open to all nursery staff.
  // It is the read-and-sign screen, and the whole point is that a document can
  // be sent to anyone who holds a platform account — so gating it to managers
  // would create obligations the person cannot discharge. As above, the API
  // gates the data: the reader only opens a request whose holder you are (or
  // that you can see as a deputy+), so an open route leaks nothing.
  "/acknowledgements",
  // People Hub profiles (NAU-425): everyone may open their own profile, and
  // deputies and above may open anyone's. As above, the API gates the data: a
  // profile the caller may not see is a 404, so an open route leaks nothing.
  "/people",
  // Emergency lighting checks (NAU-411): same shape as the extinguisher check
  // above — any nursery member may record one, and the API's ZenStack policy
  // gates the data rather than this list.
  "/emergency-light-checks",
  "/set-password",
  // Sync inspector: reachable by any nursery member. It answers "is the check I
  // recorded still waiting to send?", and the person asking that is almost
  // always the practitioner who recorded it. The page is non-production only
  // and shows just this device's own queue.
  "/sync-status",
];

function isAllowedForNurseryStaff(pathname: string): boolean {
  return NURSERY_STAFF_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

// Check if user has MANAGER role in any nursery
function hasManagerRole(nurseryAccess: Array<{ role: string }>): boolean {
  return nurseryAccess.some((access) => access.role === "MANAGER");
}

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const {
    isLoading,
    isAuthenticated,
    sessionUser,
    memberships,
    nurseryAccess,
    isPlatformAdmin,
    requiresPasswordChange,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Mounted here rather than on a page so that compliance work finishing its
  // sync refreshes the tables no matter which screen the user is on.
  useComplianceSyncRefresh();

  // Same reasoning for alert work: a sign-off queued offline lands long after
  // the manager was returned to Monitoring or the dashboard, and neither page
  // remounts while they sit on it.
  useMonitoringSyncRefresh();

  // And for follow-up actions, where the completion screen has always closed
  // before the queued status change reaches the server — leaving the manager
  // without the sign-off prompt the dashboard status carries.
  useFollowUpActionSyncRefresh();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({
        to: "/login",
        search: { redirect: location.pathname },
        replace: true,
      });
    }
  }, [isLoading, isAuthenticated, location.pathname, navigate]);

  // Redirect to set-password if user needs to set their password (first login or admin reset)
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    const isOnSetPasswordPage = location.pathname === "/set-password";

    if (requiresPasswordChange && !isOnSetPasswordPage) {
      navigate({ to: "/set-password", replace: true });
    }
  }, [
    isLoading,
    isAuthenticated,
    requiresPasswordChange,
    location.pathname,
    navigate,
  ]);

  // Check access levels and redirect if needed
  useEffect(() => {
    // Wait for session to be fully loaded before checking access
    if (isLoading || !isAuthenticated || !sessionUser) return;

    // Skip access checks if user needs to set password - they can only access /set-password
    if (requiresPasswordChange) return;

    const hasActiveMembership = memberships.some((m) => m.status === "ACTIVE");
    const hasNurseryAccess = nurseryAccess.length > 0;

    // User must have at least one access level
    if (!isPlatformAdmin && !hasActiveMembership && !hasNurseryAccess) {
      navigate({
        to: "/no-access",
        search: { restricted: false },
        replace: true,
      });
      return;
    }

    // Restrict nursery staff (non-managers) to allowed routes only
    const isNurseryLevelUser =
      !isPlatformAdmin && !hasActiveMembership && hasNurseryAccess;
    const isNurseryManager =
      isNurseryLevelUser && hasManagerRole(nurseryAccess);
    const isRestrictedNurseryStaff = isNurseryLevelUser && !isNurseryManager;

    if (
      isRestrictedNurseryStaff &&
      !isAllowedForNurseryStaff(location.pathname)
    ) {
      navigate({
        to: "/no-access",
        search: { restricted: true },
        replace: true,
      });
    }
  }, [
    isLoading,
    isAuthenticated,
    sessionUser,
    requiresPasswordChange,
    memberships,
    nurseryAccess,
    isPlatformAdmin,
    location.pathname,
    navigate,
  ]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  // Don't render if not authenticated (redirect is happening)
  if (!isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  const isOnSetPasswordPage = location.pathname === "/set-password";

  if (isOnSetPasswordPage) {
    return (
      <>
        <InactivityLogoutWatcher />
        <Outlet />
      </>
    );
  } else if (requiresPasswordChange) {
    navigate({ to: "/set-password", replace: true });
  }

  return (
    <>
      <InactivityLogoutWatcher />
      <ProtectedSidebarLayout>
        <Outlet />
      </ProtectedSidebarLayout>
    </>
  );
}
